'use client'

import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'

interface UseCheckoutCompletionOptions {
  /** Whether the checkout is active (e.g. the modal/overlay is open and the
   *  user has begun the checkout flow). When false the hook is inert. */
  active: boolean
  /** Called once when checkout is successfully confirmed. Uses a stable ref
   *  internally so re-renders of the parent don't reset timers. */
  onComplete: () => void
  /** When true, skips the "already-has-payment-method" auto-complete that
   *  fires immediately on mount. Use for upgrade flows where the user may
   *  already be on a paid plan. Default: false. */
  skipAutoComplete?: boolean
}

interface UseCheckoutCompletionResult {
  /** True once we have observed the LemonSqueezy iframe being entered. Set
   *  this to true by calling markInCheckout(). */
  inCheckout: boolean
  /** Whether the payment success state is active (post postMessage or reactive flip). */
  isSuccess: boolean
  /** True after 90 s on the success step without webhook confirmation. */
  successTimedOut: boolean
  /** Call this before navigating to the LemonSqueezy iframe to arm the
   *  postMessage and reactive detection. */
  markInCheckout: () => void
  /** Reset all state (e.g. when the checkout flow is cancelled/closed). */
  reset: () => void
}

/**
 * Encapsulates checkout-completion detection shared between
 * OnboardingCheckoutModal (Sidebar/UpgradeBanner) and PlanCards (new onboarding).
 *
 * Detection flow:
 *  1. postMessage `Checkout.Success` from the LemonSqueezy iframe fires first.
 *  2. `hasPaymentMethod` reactive query catches it if postMessage is missed.
 *  3. 90-second timeout sets successTimedOut so the user sees a hint instead
 *     of a spinner indefinitely.
 *
 * The `onComplete` callback is called after:
 *  - The webhook confirms `hasPaymentMethod === true` (2 s delay for animation).
 * It is NOT called prematurely: calling onComplete before the webhook arrives
 * would cause `finalizeOnboarding` to fail with "Payment method required".
 */
export function useCheckoutCompletion({
  active,
  onComplete,
  skipAutoComplete = false,
}: UseCheckoutCompletionOptions): UseCheckoutCompletionResult {
  // True when the user has explicitly entered the LemonSqueezy iframe.
  // Set synchronously in markInCheckout so it's guaranteed to be true before
  // any async state updates that follow.
  const inCheckoutRef = useRef(false)

  // Prevents in-flight effects from updating state after a reset.
  const cancelledRef = useRef(false)

  // Stable ref for onComplete so timer effects don't re-run on every parent render.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])

  const [inCheckout, setInCheckout] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [successTimedOut, setSuccessTimedOut] = useState(false)

  const hasPaymentMethod = useQuery(api.features.billing.queries.hasPaymentMethod)

  // --- Auto-complete when hasPaymentMethod is already true on activation ---
  // (Skipped for upgrade flows where the user may already be paid.)
  useEffect(() => {
    if (!active || skipAutoComplete) return
    if (inCheckoutRef.current) return // already in checkout — handled below
    if (hasPaymentMethod === true) {
      onCompleteRef.current()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, skipAutoComplete, hasPaymentMethod])

  // --- Detect payment completion: hasPaymentMethod flips while in checkout ---
  useEffect(() => {
    if (!active || !inCheckoutRef.current) return
    if (hasPaymentMethod === true) {
      setIsSuccess(true)
    }
  }, [active, hasPaymentMethod])

  // --- Primary detection: postMessage from LemonSqueezy iframe ---
  useEffect(() => {
    if (!active || !inCheckout) return

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('lemonsqueezy.com')) return

      let data: unknown = event.data
      if (typeof data === 'string') {
        try { data = JSON.parse(data) } catch { /* not JSON */ }
      }

      const d = data as Record<string, unknown> | string
      const isSuccessEvent =
        d === 'Checkout.Success' ||
        (typeof d === 'object' && d !== null && (
          d['event'] === 'Checkout.Success' ||
          (d['data'] as Record<string, unknown>)?.['event'] === 'Checkout.Success' ||
          d['type'] === 'checkout:complete' ||
          d['event_type'] === 'checkout.success'
        ))

      if (isSuccessEvent && inCheckoutRef.current) {
        setIsSuccess(true)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [active, inCheckout])

  // --- After success: wait for webhook confirmation then call onComplete ---
  // Uses onCompleteRef so parent re-renders don't reset the timer.
  // 90 s timeout sets successTimedOut so the user isn't stuck indefinitely.
  useEffect(() => {
    if (!isSuccess) return

    if (hasPaymentMethod === true) {
      // Webhook confirmed — proceed after the success animation.
      const timer = setTimeout(() => {
        if (!cancelledRef.current) onCompleteRef.current()
      }, 2000)
      return () => clearTimeout(timer)
    }

    // Webhook not yet confirmed — wait up to 90 s.
    const fallback = setTimeout(() => {
      if (!cancelledRef.current) setSuccessTimedOut(true)
    }, 90_000)
    return () => clearTimeout(fallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, hasPaymentMethod])

  const markInCheckout = () => {
    inCheckoutRef.current = true
    cancelledRef.current = false
    setInCheckout(true)
  }

  const reset = () => {
    cancelledRef.current = true
    inCheckoutRef.current = false
    setInCheckout(false)
    setIsSuccess(false)
    setSuccessTimedOut(false)
    // Allow future calls to go through after reset.
    setTimeout(() => { cancelledRef.current = false }, 0)
  }

  return { inCheckout, isSuccess, successTimedOut, markInCheckout, reset }
}
